import React from 'react';
import './CalendarHeader.css';

/**
 * Navigation and toolbar component for the CalendarView.
 * Handles view switching, date navigation, time/day filters, and global actions.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isSidebarOpen - Sidebar state for compact styling
 * @param {string} props.viewMode - 'month' | 'days'
 * @param {string} props.dateLabel - Formatted label for current date range
 * @param {Function} props.handleMonthNavigate - Navigates by month
 * @param {Function} props.onNavigate - Navigates by defined amount or 'today'
 * @param {number} props.navigateAmount - Days to jump per navigation
 * @param {boolean} props.showsToday - Whether today is in the current view
 * @param {Function} props.setShowRangePicker - Opens custom range modal
 * @param {boolean} props.canUndo - Undo availability
 * @param {boolean} props.canRedo - Redo availability
 * @param {Function} props.onUndo - Undo handler
 * @param {Function} props.onRedo - Redo handler
 * @param {number} props.hourStart - Visible start hour
 * @param {Function} props.setHourStart - Updates start hour
 * @param {number} props.hourEnd - Visible end hour
 * @param {Function} props.setHourEnd - Updates end hour
 * @param {number} props.viewDays - Number of visible days
 * @param {Function} props.onViewChange - Updates number of days
 * @param {Function} props.onViewModeChange - Toggles month/days view
 * @param {Function} props.onNewAssignment - Opens new assignment creation
 * @param {Function} props.onSwitchToList - Toggles list view
 * @param {Function} props.onExport - PDF export handler
 * @param {boolean} props.isReadOnly - Whether the user has personal-only access (Rank 2)
 */
export function CalendarHeader({
    isSidebarOpen,
    viewMode,
    dateLabel,
    handleMonthNavigate,
    onNavigate,
    navigateAmount,
    showsToday,
    setShowRangePicker,
    canUndo,
    canRedo,
    onUndo,
    onRedo,
    hourStart,
    setHourStart,
    hourEnd,
    setHourEnd,
    viewDays,
    onViewChange,
    onViewModeChange,
    onNewAssignment,
    onSwitchToList,
    onExport,
    isReadOnly = false
}) {
    return (
        <div className={`calendar-nav ${isSidebarOpen ? 'compact-mode' : ''}`}>
            <div className="calendar-nav-center">
                <button 
                    className="calendar-nav-btn" 
                    onClick={() => (viewMode === 'month' ? handleMonthNavigate(-1) : onNavigate(-navigateAmount))}
                    style={{ width: 32, height: 32, padding: 0, justifyContent: 'center' }}
                >
                    <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: isSidebarOpen ? 4 : 6 }}>
                    <span className="calendar-date-label" style={{ fontSize: isSidebarOpen ? '0.85rem' : '1rem' }}>{dateLabel}</span>
                    {viewMode !== 'month' && (
                        <button 
                            className="calendar-nav-btn range-trigger" 
                            onClick={() => setShowRangePicker(true)}
                            title="Bereich wählen"
                            style={{ width: 32, height: 32, padding: 0, fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            📅
                        </button>
                    )}
                </div>
                <button 
                    className="calendar-nav-btn" 
                    onClick={() => (viewMode === 'month' ? handleMonthNavigate(1) : onNavigate(navigateAmount))}
                    style={{ width: 32, height: 32, padding: 0, justifyContent: 'center' }}
                >
                    <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </button>
                {!showsToday && (
                    <button className="calendar-nav-btn" onClick={() => onNavigate('today')} style={{ fontSize: '0.78rem', fontWeight: 600 }}>
                        Heute
                    </button>
                )}
                {!isReadOnly && (canUndo || canRedo) ? (
                    <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                        <button 
                            className={`calendar-nav-btn undo ${!canUndo ? 'disabled' : ''}`} 
                            onClick={onUndo} 
                            disabled={!canUndo}
                            title="Rückgängig (⌘Z)"
                        >
                            ↩️
                        </button>
                        <button 
                            className={`calendar-nav-btn redo ${!canRedo ? 'disabled' : ''}`} 
                            onClick={onRedo} 
                            disabled={!canRedo}
                            title="Wiederherstellen (⌘Y)"
                        >
                            ↪️
                        </button>
                    </div>
                ) : null}
            </div>

            <div className={`calendar-toolbar-right ${isSidebarOpen ? 'compact-mode' : ''}`} style={{ display: 'flex', gap: isSidebarOpen ? 6 : 10, alignItems: 'center' }}>
                {/* Time filter - visible only in Days view */}
                {viewMode !== 'month' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f1f5f9', padding: isSidebarOpen ? '4px 6px' : '4px 8px', borderRadius: 6 }}>
                        {!isSidebarOpen && <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b' }}>ZEIT:</span>}
                        <input
                            type="number" min="0" max="23" value={hourStart}
                            onChange={(e) => setHourStart(Math.min(parseInt(e.target.value) || 0, hourEnd - 1))}
                            style={{ width: 44, border: 'none', background: 'white', color: '#0f172a', borderRadius: 4, fontSize: '0.85rem', fontWeight: 600, textAlign: 'center', padding: '2px 0' }}
                        />
                        <span style={{ color: '#94a3b8' }}>-</span>
                        <input
                            type="number" min="1" max="24" value={hourEnd}
                            onChange={(e) => setHourEnd(Math.max(parseInt(e.target.value) || 24, hourStart + 1))}
                            style={{ width: 44, border: 'none', background: 'white', color: '#0f172a', borderRadius: 4, fontSize: '0.85rem', fontWeight: 600, textAlign: 'center', padding: '2px 0' }}
                        />
                        <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', marginLeft: 2 }}>Uhr</span>
                    </div>
                )}

                {/* Tage filter - decoupled from toggle */}
                {viewMode !== 'month' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: isSidebarOpen ? '4px 6px' : '4px 8px', background: '#f1f5f9', borderRadius: 6 }}>
                        {!isSidebarOpen && <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Tage:</span>}
                        {isSidebarOpen && <span style={{ fontSize: '0.85rem' }}>📅</span>}
                        <input
                            type="number"
                            min="1"
                            max="100"
                            value={viewDays}
                            onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (!isNaN(val) && val > 0) onViewChange(val);
                            }}
                            style={{
                                width: 38,
                                padding: '2px 0',
                                border: 'none',
                                background: 'white',
                                borderRadius: 4,
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                color: '#1e293b',
                                outline: 'none',
                                textAlign: 'center'
                            }}
                        />
                    </div>
                )}

                {/* Standalone View Toggle Button */}
                <button
                    className={`view-toggle-btn ${viewMode === 'month' ? 'active' : ''}`}
                    onClick={() => onViewModeChange(viewMode === 'month' ? 'days' : 'month')}
                    style={{ minWidth: isSidebarOpen ? '80px' : '100px', justifyContent: 'center', padding: isSidebarOpen ? '4px 8px' : '6px 12px' }}
                >
                    {viewMode === 'month' ? (isSidebarOpen ? '📅 Tage' : '📅 Ansicht: Tage') : (isSidebarOpen ? '🗓️ Monat' : '🗓️ Ansicht: Monat')}
                </button>

                 {!isReadOnly && (
                    <button className="btn-new-assignment" onClick={onNewAssignment} style={{ padding: isSidebarOpen ? '6px 10px' : '8px 16px' }}>
                        {isSidebarOpen ? '+ Einsatz' : '+ Neuer Einsatz'}
                    </button>
                )}
                {onSwitchToList && (
                    <button
                        className="view-toggle-btn"
                        onClick={onSwitchToList}
                        style={{ padding: isSidebarOpen ? '4px 8px' : '6px 12px' }}
                    >
                        {isSidebarOpen ? '📋' : '📋 Liste'}
                    </button>
                )}
                {onExport && (
                     <button 
                        className="view-toggle-btn" 
                        onClick={onExport}
                         style={{ marginLeft: '4px', padding: isSidebarOpen ? '4px 8px' : '6px 12px' }}
                      >
                         {isSidebarOpen ? '📄' : '📄 PDF'}
                      </button>
                )}
            </div>
        </div>
    );
}
